import React from 'react';
import PropTypes from 'prop-types';
import { LuArrowRight, LuCircleCheck, LuClipboardList, LuPackage, LuTriangleAlert } from 'react-icons/lu';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { CheckRow, Meter, Notice, PANEL, fieldAnchor } from '../VisitUi';

/*
 * The to-do items, grouped as before. Keys are the Firestore fields under
 * Visits/{id}.todoList - only the wording shown next to them is in the locale.
 */
export const TODO_GROUPS = [
  {
    key: 'pre',
    items: [
      'pre_patientCalled',
      'pre_nurseTransferArranged',
      'pre_courierArranged',
      'pre_hospitalApproval',
      'pre_nurseEquipmentReady',
      'pre_accommodationPlanned',
    ],
  },
  {
    key: 'visit',
    items: [
      'patientConsent',
      'protocolUpdate',
      'kvkkApproval',
      'visitNumberCheck',
      'projectCheck',
      'workflowCheck',
      'adverseEventCheck',
      'medicationCheck',
      'sourceDocuments',
      'sourceDocumentsApproval',
      'sourceDocumentsShared',
      'transferForm',
      'documentShipping',
      'stockEntryRequired',
    ],
  },
];

const ALL_ITEMS = TODO_GROUPS.flatMap((group) => group.items);

/** "Yapılacaklar": every tick is written to Firestore at once (handleTodoChange). */
const TodoStep = ({ todoList, onTodoChange, isReadOnly, stockCount, onGoToStock }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`visitModal.todo.${key}`, vars);
  const doneCount = ALL_ITEMS.filter((key) => todoList[key] === true).length;

  return (
    <div className="tw-space-y-4">
      <Notice tone="accent" icon={LuCircleCheck}>{label('autosaveNote')}</Notice>

      <div className="tw-flex tw-items-center tw-gap-3 tw-px-1">
        <Meter percent={(doneCount / ALL_ITEMS.length) * 100} className="tw-flex-1" />
        <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">
          {label('counter', { done: doneCount, total: ALL_ITEMS.length })}
        </span>
      </div>

      {TODO_GROUPS.map((group, groupIndex) => {
        const groupDone = group.items.filter((key) => todoList[key] === true).length;
        return (
          <section
            key={group.key}
            id={groupIndex === 0 ? fieldAnchor('anyTodo') : undefined}
            className={`${PANEL} tw-p-4 sm:tw-p-5`}
          >
            <header className="tw-mb-3.5 tw-flex tw-items-start tw-gap-3">
              <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-teal-500/10 tw-text-teal-700 tw-ring-1 tw-ring-inset tw-ring-teal-500/25 dark:tw-text-teal-300">
                <LuClipboardList aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
              </span>
              <div className="tw-min-w-0 tw-flex-1">
                <h3 className="tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label(`groups.${group.key}.title`)}</h3>
                <p className="tw-mt-0.5 tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">{label(`groups.${group.key}.hint`)}</p>
              </div>
              <span className={`tw-shrink-0 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-tabular-nums tw-ring-1 tw-ring-inset ${
                groupDone === group.items.length
                  ? 'tw-bg-emerald-500/10 tw-text-emerald-700 tw-ring-emerald-500/25 dark:tw-text-emerald-300'
                  : 'tw-bg-slate-500/10 tw-text-slate-600 tw-ring-slate-500/20 dark:tw-text-slate-300'
              }`}
              >
                {groupDone} / {group.items.length}
              </span>
            </header>

            <div className="tw-grid tw-grid-cols-1 tw-gap-2 xl:tw-grid-cols-2">
              {group.items.map((key) => (
                key === 'stockEntryRequired' ? (
                  // Ticked → the visit can't be completed until the Stock step has a line
                  <div key={key} className="xl:tw-col-span-2">
                    <CheckRow
                      id={`vm-todo-${key}`}
                      label={label(`items.${key}`)}
                      checked={Boolean(todoList[key])}
                      onChange={(value) => onTodoChange(key, value)}
                      disabled={isReadOnly}
                    >
                      <p className="tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{label('stockHint')}</p>
                      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        <button
                          type="button"
                          onClick={onGoToStock}
                          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-violet-500/10 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-violet-700 tw-transition-colors hover:tw-bg-violet-500/15 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-violet-500/40 dark:tw-text-violet-300"
                        >
                          <LuPackage aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                          {label('goToStock')}
                          <LuArrowRight aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        </button>
                        {todoList[key] && (
                          <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold ${stockCount > 0 ? 'tw-text-emerald-700 dark:tw-text-emerald-300' : 'tw-text-rose-600 dark:tw-text-rose-300'}`}>
                            {stockCount > 0
                              ? <LuCircleCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                              : <LuTriangleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
                            {stockCount > 0 ? label('stockDone', { count: stockCount }) : label('stockPending')}
                          </span>
                        )}
                      </div>
                    </CheckRow>
                  </div>
                ) : (
                  <CheckRow
                    key={key}
                    id={`vm-todo-${key}`}
                    label={label(`items.${key}`)}
                    checked={Boolean(todoList[key])}
                    onChange={(value) => onTodoChange(key, value)}
                    disabled={isReadOnly}
                  />
                )
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

TodoStep.propTypes = {
  todoList: PropTypes.object.isRequired,
  onTodoChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  stockCount: PropTypes.number.isRequired,
  onGoToStock: PropTypes.func.isRequired,
};

export default TodoStep;
